import { MRT_ColumnDef } from "material-react-table"
import { useMemo } from "react"
import { format } from "date-fns"
import { useTheme } from '@mui/material';

interface UseTableColumnsReporteContratosPendientesProps {
  pagination?: { pageIndex: number; pageSize: number };
}

export const useTableColumnsReporteContratosPendientes = (props?: UseTableColumnsReporteContratosPendientesProps) => {
  const { pagination } = props || {};

  const columnBase = useMemo<MRT_ColumnDef<any>[]>(() => [
    {
      accessorKey: 'numero',
      header: '#',
      size: 60,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        // Calcular número absoluto basado en la página actual
        const baseNumber = pagination ? (pagination.pageIndex * pagination.pageSize) : 0;
        return baseNumber + row.index + 1;
      },
    },
    {
      accessorKey: 'idContrato',
      header: 'Contrato #',
      size: 100,
      Cell: ({ cell }) => cell.getValue<number>() || '-',
    },
    {
      // Acceso directo a propiedad anidada para el sort/filter automático
      accessorKey: 'instalacion.numero_referencia',
      header: 'Contrato Eva (Ref)',
      size: 140,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      accessorKey: 'fecha_instalacion',
      header: 'Fecha Instalación',
      size: 150,
      Cell: ({ cell }) => {
        const fecha = cell.getValue() as string;
        if (fecha) {
          try {
            // Se agrega 'T00:00:00' para evitar problemas de zona horaria al parsear "YYYY-MM-DD"
            return format(new Date(`${fecha}T00:00:00`), 'dd/MM/yyyy');
          } catch {
            return fecha;
          }
        }
        return '-';
      },
    },
    {
      accessorKey: 'fecha_subida',
      header: 'Fecha Subida Contrato',
      size: 150,
      Cell: ({ cell }) => {
        const fecha = cell.getValue() as string;
        if (fecha) {
          try {
            return format(new Date(`${fecha}T00:00:00`), 'dd/MM/yyyy');
          } catch {
            return fecha;
          }
        }
        return '-';
      },
    },
    {
      accessorKey: 'instalacion.cedula_identidad',
      header: 'Documento',
      size: 140,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      accessorKey: 'nombre_cliente',
      header: 'Cliente',
      size: 250,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      accessorKey: 'estado_servicio',
      header: 'Estado del Servicio SOFTNET',
      size: 250,
      Cell: ({ cell }) => {
        const theme = useTheme();
        const value = cell.getValue<string>();

        return (
          <span
            style={{
              textTransform: 'uppercase',
              color: theme.palette.warning.main,
            }}
          >
            {value || '-'}
          </span>
        );
      },
    },
    {
      accessorKey: 'estadocontrato.descripcion',
      header: 'Estado del contrato SOFTNET',
      size: 250,
      Cell: ({ cell }) => {
        const theme = useTheme();
        const value = cell.getValue<string>();

        return (
          <span
            style={{
              textTransform: 'uppercase',
              color: theme.palette.warning.main,
            }}
          >
            {value || '-'}
          </span>
        );
      },
    },
    {
      accessorKey: 'ciudad.Ciudad',
      header: 'Ciudad',
      size: 200,
      Cell: ({ cell }) => {
        const value = cell.getValue<string>();
        return value ? value.toUpperCase() : '-';
      },
    },
    {
      accessorKey: 'tecnico.nombre_tecnico',
      header: 'Tecnico',
      size: 200,
      Cell: ({ cell }) => {
        const value = cell.getValue<string>();
        return value ? value.toUpperCase() : '-';
      },
    },
    {
      accessorKey: 'instalacion.costo_instalacion',
      header: 'Detalle Pago',
      size: 120,
      Cell: ({ cell }) => {
        const valor = cell.getValue<string>();
        return valor ? `$${valor}` : '$0.00';
      },
    },
    {
      accessorKey: 'vendedor',
      header: 'Vendedor',
      size: 200,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      accessorKey: 'instalacion.vendedor.canalventa.descripcion',
      header: 'Area',
      size: 200,
      Cell: ({ cell }) => {
        const value = cell.getValue<string>();
        return value ? value.toUpperCase() : '-';
      },
    },
    {
      accessorKey: 'instalacion.contrato_eva.observaciones_eva.observacion_tecnico',
      header: 'Observación Técnico',
      size: 250,
      Cell: ({ row }) => {
        const obs = row.original.instalacion?.contrato_eva?.observaciones_eva?.observacion_tecnico;
        return obs || '-';
      },
    },
    {
      accessorKey: 'instalacion.contrato_eva.observaciones_eva.observacion_ventas',
      header: 'Observación Ventas',
      size: 250,
      Cell: ({ row }) => {
        // Acceso seguro a propiedades profundamente anidadas
        const obs = row.original.instalacion?.contrato_eva?.observaciones_eva?.observacion_ventas;
        return obs || '-';
      },
    },
    {
      accessorKey: 'instalacion.contrato_eva.estado_actualizacion',
      header: 'Estado Contrato EVA',
      size: 250,
      Cell: ({ cell }) => {
        const theme = useTheme();
        const value = cell.getValue<string>();

        return (
          <span
            style={{
              textTransform: 'uppercase',
              color: theme.palette.success.main,
            }}
          >
            {value || '-'}
          </span>
        );
      },
    },
    {
      accessorKey: 'instalacion.contrato_eva.fecha_modificacion',
      header: 'Fecha Última Actualización',
      size: 150,
      Cell: ({ cell }) => {
        const fecha = cell.getValue() as string;
        if (fecha) {
          try {
            return format(new Date(`${fecha}T00:00:00`), 'dd/MM/yyyy');
          } catch {
            return fecha;
          }
        }
        return '-';
      },
    }
  ], [pagination]);

  return {
    columnBase
  }
}
